import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { AxiosError } from 'axios'
import { IContact, IContactCategory, IContactType } from '../../features/Contacts/types'
import { IDocumentCategory } from '../../features/Documents/types'
import { IDocumentType } from '../../features/DocumentType/types'
import { ILocationByTenantCustomer, ITransportMode } from '../../features/Shipments/types'
import { IAssetMeasureUnit } from '../../features/types'
import { LOGIKSPARK_CORE_API_URL } from '../axios/const'
import { fetchData } from './fetchData'

const apiAxiosBaseQuery =
  ({
    baseUrl, // eslint-disable-line @typescript-eslint/no-unused-vars
  }: {
    baseUrl: string
  }): BaseQueryFn<
    {
      url: string
      method: 'GET' | 'POST' | 'PUT' | 'DELETE'
      domain: 'Core'
      body?: any
    },
    unknown,
    unknown
  > =>
  async ({ url, method, domain, body }) => {
    try {
      return await fetchData(domain, method, url, body)
    } catch (axiosError) {
      const err = axiosError as AxiosError
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

export const apiCoreSlice = createApi({
  reducerPath: 'apiCore',
  baseQuery: apiAxiosBaseQuery({
    baseUrl: LOGIKSPARK_CORE_API_URL,
  }),
  tagTypes: [
    'DocumentCategory',
    'DocumentType',
    'TransportMode',
    'ContactType',
    'ContactCategory',
    'Locations',
    'Country',
    'TemperatureRanges',
    'MeasureUnits',
  ],
  endpoints(build) {
    return {
      // Core //
      // Documents //
      getDocumentCategory: build.query<
        IDocumentCategory,
        { tenantId?: number; customerId?: number; categoryId: number }
      >({
        query: ({ tenantId, customerId, categoryId }) => {
          let url
          if (customerId) {
            url = `/customer/${customerId}/document-category/${categoryId}`
          } else if (tenantId) {
            url = `/tenant/${tenantId}/document-category/${categoryId}`
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
          return {
            url,
            method: 'GET',
            domain: 'Core',
          }
        },
        providesTags: (result, error, { categoryId }) => [
          { type: 'DocumentCategory', id: categoryId },
        ],
      }),
      getSASForUploadFile: build.query<string, void>({
        query: () => ({
          url: '/document/sas/upload',
          method: 'GET',
          domain: 'Core',
        }),
        keepUnusedDataFor: 0,
      }),
      getSASForReadFile: build.query<string, void>({
        query: () => ({
          url: '/document/sas/read',
          method: 'GET',
          domain: 'Core',
        }),
        keepUnusedDataFor: 0,
      }),
      getSASForDeleteFile: build.query<string, void>({
        query: () => ({
          url: '/document/sas/delete',
          method: 'GET',
          domain: 'Core',
        }),
        keepUnusedDataFor: 0,
      }),
      getDocumentsByEntityId: build.query<any, { entityId: number; categoryId: number }>({
        query: ({ entityId, categoryId }) => {
          const categoryRouteMap: Record<number, string> = {
            1: 'company',
            2: 'asset',
            3: 'product',
            4: 'product-price',
          }
          const category = categoryRouteMap[categoryId] ?? 'company'
          return {
            url: `/document/${category}/${entityId}`,
            method: 'GET',
            domain: 'Core',
          }
        },
      }),
      // Documents //
      // Contacts //
      getContactCategory: build.query<
        IContactCategory[],
        { tenantId?: number; customerId?: number }
      >({
        query: ({ tenantId, customerId }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/contact-category`,
              method: 'GET',
              domain: 'Core',
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/contact-category`,
              method: 'GET',
              domain: 'Core',
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
      }),
      getContactType: build.query<
        IContactType[],
        { contactCategoryId: number; tenantId?: number; customerId?: number }
      >({
        query: ({ tenantId, customerId, contactCategoryId }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/contactCategory/${contactCategoryId}/contact-type`,
              method: 'GET',
              domain: 'Core',
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/contactCategory/${contactCategoryId}/contact-type`,
              method: 'GET',
              domain: 'Core',
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        providesTags: () => [{ type: 'ContactType', id: 'ContactType' }],
      }),
      getContactByEntityId: build.query<IContact[], number>({
        query: (entityId) => ({
          url: `/contact/${entityId}`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'ContactType', id: 'ContactType' }],
      }),
      getContactTypesByTenantId: build.query<IContactType[], number>({
        query: (tenantId) => ({
          url: `/tenant/${tenantId}/contact-type`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'ContactType', id: 'ContactType' }],
      }),
      getContactTypesByCustomerId: build.query<IContactType[], number>({
        query: (customerId) => ({
          url: `/customer/${customerId}/contact-type`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'ContactType', id: 'ContactType' }],
      }),
      addContactType: build.mutation<IContactType, IContactType>({
        query: (contactType) => ({
          url: '/contact-type',
          method: 'POST',
          domain: 'Core',
          body: contactType,
        }),
        invalidatesTags: [
          { type: 'ContactType', id: 'ContactType' },
          { type: 'ContactCategory', id: 'ContactCategory' },
        ],
      }),
      updateContactType: build.mutation<IContactType, IContactType>({
        query: (contactType) => ({
          url: '/contact-type',
          method: 'PUT',
          domain: 'Core',
          body: contactType,
        }),
        invalidatesTags: [
          { type: 'ContactType', id: 'ContactType' },
          { type: 'ContactCategory', id: 'ContactCategory' },
        ],
      }),
      deleteContactType: build.mutation<IContactType, number>({
        query: (id) => ({
          url: `/contact-type/${id}`,
          method: 'DELETE',
          domain: 'Core',
        }),
        invalidatesTags: [
          { type: 'ContactType', id: 'ContactType' },
          { type: 'ContactCategory', id: 'ContactCategory' },
        ],
      }),
      addContactCategory: build.mutation<IContactCategory, IContactCategory>({
        query: (contactCategory) => ({
          url: '/contact-category',
          method: 'POST',
          domain: 'Core',
          body: contactCategory,
        }),
        invalidatesTags: [{ type: 'ContactCategory', id: 'ContactCategory' }],
      }),
      updateContactCategory: build.mutation<IContactCategory, IContactCategory>({
        query: (contactCategory) => ({
          url: '/contact-category',
          method: 'PUT',
          domain: 'Core',
          body: contactCategory,
        }),
        invalidatesTags: [{ type: 'ContactCategory', id: 'ContactCategory' }],
      }),
      deleteContactCategory: build.mutation<IContactCategory, number>({
        query: (id) => ({
          url: `/contact-category/${id}`,
          method: 'DELETE',
          domain: 'Core',
        }),
        invalidatesTags: [{ type: 'ContactCategory', id: 'ContactCategory' }],
      }),
      getContactCategoriesByTenantId: build.query<IContactCategory[], number>({
        query: (tenantId) => ({
          url: `/tenant/${tenantId}/contact-category`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'ContactCategory', id: 'ContactCategory' }],
      }),
      getContactCategoriesByCustomerId: build.query<IContactCategory[], number>({
        query: (customerId) => ({
          url: `/customer/${customerId}/contact-category`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'ContactCategory', id: 'ContactCategory' }],
      }),
      // Contacts //
      getLocations: build.query<
        ILocationByTenantCustomer[],
        {
          tenantId?: number
          includeCustomerLocations?: boolean
          customerId?: number
          locationTypeId?: number
        }
      >({
        query: ({ tenantId, includeCustomerLocations, customerId, locationTypeId }) => {
          let query = ''
          if (includeCustomerLocations) {
            query += '?include-customer-locations=true'
          }
          if (locationTypeId) {
            query += query ? '&' : '?'
            query += `location-type-id=${locationTypeId}`
          }
          if (customerId) {
            return {
              url: `/customer/${customerId}/location${query}`,
              method: 'GET',
              domain: 'Core',
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/location${query}`,
              method: 'GET',
              domain: 'Core',
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        providesTags: () => [{ type: 'Locations', id: 'Locations' }],
      }),
      createLocation: build.mutation<any, { tenantId?: number; customerId?: number; body: any }>({
        query: ({ tenantId, customerId, body }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/location`,
              method: 'POST',
              domain: 'Core',
              body,
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/location`,
              method: 'POST',
              domain: 'Core',
              body,
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        invalidatesTags: (result, error, { tenantId, customerId }) => [
          { type: 'Locations', id: customerId || tenantId },
          { type: 'Locations', id: 'Locations' },
        ],
      }),
      updateLocation: build.mutation<any, { tenantId?: number; customerId?: number; body: any }>({
        query: ({ tenantId, customerId, body }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/location`,
              method: 'PUT',
              domain: 'Core',
              body,
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/location`,
              method: 'PUT',
              domain: 'Core',
              body,
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        invalidatesTags: (result, error, { tenantId, customerId }) => [
          { type: 'Locations', id: customerId || tenantId },
          { type: 'Locations', id: 'Locations' },
        ],
      }),
      deleteLocation: build.mutation<void, { locationId: number; tenantId: number }>({
        query: ({ locationId, tenantId }) => ({
          url: `tenant/${tenantId}/location/${locationId}`,
          method: 'DELETE',
          domain: 'Core',
        }),
        invalidatesTags: () => [{ type: 'Locations', id: 'Locations' }],
      }),
      locationById: build.query<
        ILocationByTenantCustomer,
        { locationId: number; tenantId: number; customerId?: number }
      >({
        query: ({ locationId }) => {
          return {
            url: `/location/${locationId}`,
            method: 'GET',
            domain: 'Core',
          }
        },
        providesTags: (result, error, locationId) => [
          { type: 'Locations', locationId },
          { type: 'Locations', id: 'Locations' },
        ],
      }),
      getTransportMode: build.query<ITransportMode[], void>({
        query: () => {
          return {
            url: '/transport-mode',
            method: 'GET',
            domain: 'Core',
          }
        },
        providesTags: (result, error) => [{ type: 'TransportMode', id: 'any' }],
      }),
      getAllTransportModes: build.query<ITransportMode[], void>({
        query: () => ({
          url: 'transport-mode',
          method: 'GET',
          domain: 'Core',
        }),
      }),
      getCountries: build.query<any, void>({
        query: () => ({
          url: '/countries',
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'Country', id: 'Country' }],
      }),
      getTemperatureRanges: build.query<any, void>({
        query: () => ({
          url: '/get-temperature-ranges',
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'TemperatureRanges', id: 'TemperatureRanges' }],
      }),
      getMeasureUnits: build.query<IAssetMeasureUnit[], void>({
        query: () => {
          return {
            url: '/get-all-measure-units',
            method: 'GET',
            domain: 'Core',
          }
        },
        providesTags: () => [{ type: 'MeasureUnits' }],
      }),

      // Document Types endpoints
      getTenantDocumentType: build.query<IDocumentType, { tenantId: number; id: number }>({
        query: ({ tenantId, id }) => ({
          url: `/tenant/${tenantId}/document-type/${id}`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: (result, error, { id }) => [{ type: 'DocumentType', id }],
      }),
      getCustomerDocumentType: build.query<IDocumentType, { customerId: number; id: number }>({
        query: ({ customerId, id }) => ({
          url: `/customer/${customerId}/document-type/${id}`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: (result, error, { id }) => [{ type: 'DocumentType', id }],
      }),
      getTenantDocumentTypes: build.query<IDocumentType[], { tenantId: number }>({
        query: ({ tenantId }) => ({
          url: `/tenant/${tenantId}/document-types`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'DocumentType', id: 'LIST' }],
      }),
      getCustomerDocumentTypes: build.query<IDocumentType[], { customerId: number }>({
        query: ({ customerId }) => ({
          url: `/customer/${customerId}/document-types`,
          method: 'GET',
          domain: 'Core',
        }),
        providesTags: () => [{ type: 'DocumentType', id: 'LIST' }],
      }),
      createTenantDocumentType: build.mutation<
        IDocumentType,
        { tenantId: number; documentType: IDocumentType }
      >({
        query: ({ tenantId, documentType }) => ({
          url: `/tenant/${tenantId}/document-type`,
          method: 'POST',
          domain: 'Core',
          body: documentType,
        }),
        invalidatesTags: () => [{ type: 'DocumentType', id: 'LIST' }],
      }),
      createCustomerDocumentType: build.mutation<
        IDocumentType,
        { customerId: number; documentType: IDocumentType }
      >({
        query: ({ customerId, documentType }) => ({
          url: `/customer/${customerId}/document-type`,
          method: 'POST',
          domain: 'Core',
          body: documentType,
        }),
        invalidatesTags: () => [{ type: 'DocumentType', id: 'LIST' }],
      }),
      updateTenantDocumentType: build.mutation<
        IDocumentType,
        { tenantId: number; id: number; documentType: IDocumentType }
      >({
        query: ({ tenantId, id, documentType }) => ({
          url: `/tenant/${tenantId}/document-type/${id}`,
          method: 'PUT',
          domain: 'Core',
          body: documentType,
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: 'DocumentType', id },
          { type: 'DocumentType', id: 'LIST' },
        ],
      }),
      updateCustomerDocumentType: build.mutation<
        IDocumentType,
        { customerId: number; id: number; documentType: IDocumentType }
      >({
        query: ({ customerId, id, documentType }) => ({
          url: `/customer/${customerId}/document-type/${id}`,
          method: 'PUT',
          domain: 'Core',
          body: documentType,
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: 'DocumentType', id },
          { type: 'DocumentType', id: 'LIST' },
        ],
      }),
      getAllDocumentCategories: build.query<IDocumentCategory[], void>({
        query: () => {
          return {
            url: 'document-category',
            method: 'GET',
            domain: 'Core',
          }
        },
        providesTags: (result, error) => {
          return result
            ? result.map((category) => ({ type: 'DocumentCategory', id: category.id }))
            : [{ type: 'DocumentCategory', id: 'LIST' }]
        },
      }),
      deleteTenantDocumentType: build.mutation<
        void,
        { tenantId: number; id: number }
      >({
        query: ({ tenantId, id }) => ({
          url: `/tenant/${tenantId}/document-type/${id}`,
          method: 'DELETE',
          domain: 'Core',
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: 'DocumentType', id },
          { type: 'DocumentType', id: 'LIST' },
        ],
      }),
      deleteCustomerDocumentType: build.mutation<
        void,
        { customerId: number; id: number }
      >({
        query: ({ customerId, id }) => ({
          url: `/customer/${customerId}/document-type/${id}`,
          method: 'DELETE',
          domain: 'Core',
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: 'DocumentType', id },
          { type: 'DocumentType', id: 'LIST' },
        ],
      }),
    }
  },
})

export const {
  useGetDocumentCategoryQuery,
  useGetSASForReadFileQuery,
  useGetSASForUploadFileQuery,
  useGetSASForDeleteFileQuery,
  useLazyGetSASForReadFileQuery,
  useLazyGetSASForUploadFileQuery,
  useLazyGetSASForDeleteFileQuery,
  useGetDocumentsByEntityIdQuery,
  useGetContactCategoryQuery,
  useGetContactTypeQuery,
  useGetContactByEntityIdQuery,
  useGetContactTypesByTenantIdQuery,
  useGetContactTypesByCustomerIdQuery,
  useAddContactTypeMutation,
  useUpdateContactTypeMutation,
  useDeleteContactTypeMutation,
  useAddContactCategoryMutation,
  useUpdateContactCategoryMutation,
  useDeleteContactCategoryMutation,
  useGetContactCategoriesByTenantIdQuery,
  useGetContactCategoriesByCustomerIdQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  useGetLocationsQuery,
  useLocationByIdQuery,
  useGetTransportModeQuery,
  useGetAllTransportModesQuery,
  useGetCountriesQuery,
  useGetTemperatureRangesQuery,
  useGetMeasureUnitsQuery,
  useGetTenantDocumentTypeQuery,
  useGetCustomerDocumentTypeQuery,
  useGetTenantDocumentTypesQuery,
  useGetCustomerDocumentTypesQuery,
  useCreateTenantDocumentTypeMutation,
  useCreateCustomerDocumentTypeMutation,
  useUpdateTenantDocumentTypeMutation,
  useUpdateCustomerDocumentTypeMutation,
  useGetAllDocumentCategoriesQuery,
  useDeleteTenantDocumentTypeMutation,
  useDeleteCustomerDocumentTypeMutation,
  endpoints: apiEndpoints,
} = apiCoreSlice
